/**
 * SQUAD_PACK wizard — admin creates a Squad bundle by picking already-
 * ingested teams + players from the SYSTEM catalog. Backend copies each
 * pick into PackTeam + PackPlayer template rows; the existing
 * _clone_pack_contents path clones into the buyer's tenant on purchase.
 *
 * Composition flow (option C — pick team, prefill master_squad, edit):
 *   1. Fill in pack details on the sidebar.
 *   2. Add one or more teams from the SYSTEM catalog. Each pick auto-
 *      prefills its master_squad players into a per-team roster card
 *      below; the curator can add / remove players per team.
 *   3. Hit Create & publish.
 *
 * Opens when /admin/marketplace mounts with ?action=new&type=SQUAD_PACK.
 *
 * 2026-07-18a — CSV upload → catalog picker.
 * 2026-07-18b — full-screen layout via WizardShell (sidebar + main),
 *               each picked team rendered as a large standalone card.
 */
import { useEffect, useState } from "react";
import {
  Sparkles, CheckCircle2, Users, ChevronDown, ChevronRight,
  Trash2, RotateCcw, Save,
} from "lucide-react";
import {
  createPack,
  updatePack,
  publishPack,
  listCatalogTeams,
  listCatalogPlayers,
  selectSquadFromCatalog,
} from "../../api/owner.api";
import CatalogPicker from "../../components/marketplace/CatalogPicker";
import PackDetailsForm from "../../components/marketplace/PackDetailsForm";
import WizardShell from "../../components/marketplace/WizardShell";

const ROLE_TONE = {
  BATTER:        "bg-blue-500/15 text-blue-300 border-blue-400/30",
  BOWLER:        "bg-button-primary/20 text-button-primary border-button-primary/40",
  ALL_ROUNDER:   "bg-amber-500/15 text-amber-300 border-amber-400/30",
  WICKET_KEEPER: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
};

function playerDisplay(p) {
  const parts = [p.first_name, p.middle_name, p.last_name].filter(Boolean);
  return p.nick_name || parts.join(" ").trim() || "(unnamed)";
}
function roleBadge(role) {
  if (!role) return "";
  if (role === "ALL_ROUNDER") return "AR";
  if (role === "WICKET_KEEPER") return "WK";
  return role.slice(0, 3);
}

export default function SquadPackWizard({ onClose, onCreated }) {
  const [details, setDetails] = useState({
    name: "",
    season: "",
    short_description: "",
    description: "",
    price_credits: 99,
  });
  const [teamIds, setTeamIds] = useState([]);
  const [teamById, setTeamById] = useState({});
  const [playerIdsByTeam, setPlayerIdsByTeam] = useState({});
  const [collapsed, setCollapsed] = useState({}); // teamId → bool
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [testMode, setTestMode] = useState(true);
  // Draft persistence — packId is set after the first Save so subsequent
  // Save / Publish clicks reuse the row (create is a POST that would 409
  // on the unique slug). savedAt drives the sidebar "Draft saved" hint.
  const [packId, setPackId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // Reconcile per-team roster map when a team is removed via the chip X.
  useEffect(() => {
    setPlayerIdsByTeam((prev) => {
      const next = {};
      for (const id of teamIds) next[id] = prev[id] || [];
      return next;
    });
    setCollapsed((prev) => {
      const next = {};
      for (const id of teamIds) next[id] = prev[id] || false;
      return next;
    });
  }, [teamIds]);

  function handleTeamToggle(row, nowOn) {
    const id = String(row.id);
    if (nowOn) {
      setTeamById((prev) => ({ ...prev, [id]: row }));
      setPlayerIdsByTeam((prev) =>
        prev[id] && prev[id].length
          ? prev
          : { ...prev, [id]: (row.master_squad_ids || []).map(String) },
      );
    }
  }
  function setPlayersForTeam(teamId, ids) {
    setPlayerIdsByTeam((prev) => ({ ...prev, [teamId]: ids }));
  }
  function removeTeam(teamId) {
    setTeamIds((prev) => prev.filter((x) => x !== teamId));
  }
  function resetToMasterSquad(teamId) {
    const t = teamById[teamId];
    if (!t) return;
    setPlayerIdsByTeam((prev) => ({
      ...prev,
      [teamId]: (t.master_squad_ids || []).map(String),
    }));
  }
  function toggleCollapse(teamId) {
    setCollapsed((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  }

  function detailsValid() {
    return details.name.trim().length >= 3
      && Number.isFinite(Number(details.price_credits))
      && Number(details.price_credits) >= 0;
  }

  const totalPlayers = teamIds.reduce(
    (n, id) => n + (playerIdsByTeam[id]?.length || 0), 0,
  );

  function _packBody() {
    return {
      name: details.name.trim(),
      season: details.season.trim(),
      short_description: details.short_description.trim(),
      description: details.description.trim(),
      price_credits: Number(details.price_credits),
    };
  }

  // Persist the current composition. Creates the pack on first call, PATCHes
  // details on subsequent calls, then always re-runs select-squad (the
  // backend wipes + re-writes so the founder's latest picks win). Returns
  // { pack, summary }.
  async function _persist() {
    let pack;
    if (packId) {
      const { data } = await updatePack(packId, _packBody());
      pack = data;
    } else {
      const slug = details.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80);
      const { data } = await createPack({
        ..._packBody(),
        slug,
        pack_type: "SQUAD_PACK",
      });
      pack = data;
      setPackId(pack.id);
    }

    const payloadTeams = teamIds.map((id) => ({
      team_id: id,
      player_ids: playerIdsByTeam[id] || [],
    }));
    const { data: summary } = await selectSquadFromCatalog(pack.id, payloadTeams);
    return { pack, summary };
  }

  function _errorMessage(e) {
    return e?.response?.data
      ? (typeof e.response.data === "string"
          ? e.response.data
          : (e.response.data.detail
              || e.response.data.error
              || JSON.stringify(e.response.data)))
      : e.message;
  }

  function _validate() {
    if (!detailsValid()) {
      setError("Fill in name (≥ 3 chars) and a non-negative price first.");
      return false;
    }
    if (teamIds.length === 0) {
      setError("Pick at least one team from the catalog.");
      return false;
    }
    return true;
  }

  async function handleSaveDraft() {
    if (!_validate()) return;
    setBusy(true);
    setError(null);
    try {
      const { pack } = await _persist();
      setSavedAt(new Date());
      if (onCreated) onCreated(pack);
    } catch (e) {
      setError(_errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleCreate() {
    if (!_validate()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { pack, summary } = await _persist();
      const { data: published } = await publishPack(pack.id, {
        skipPromotion: testMode,
      });

      setResult({
        pack_name: published.name,
        pack_id: pack.id,
        teams_created: summary.teams_created || 0,
        players_created: summary.players_created || 0,
        errors: summary.errors || [],
        teams: summary.teams || [],
        test_mode: testMode,
      });
      if (onCreated) onCreated(pack);
    } catch (e) {
      setError(_errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const sidebar = result ? (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold uppercase tracking-widest text-text-secondary">
        Squad pack created
      </h3>
      <ul className="text-sm text-text-primary space-y-1">
        <li>{result.teams_created} team{result.teams_created === 1 ? "" : "s"}</li>
        <li>{result.players_created} player{result.players_created === 1 ? "" : "s"}</li>
        <li className="text-text-secondary">
          {result.test_mode ? "Published in test mode." : "Promotion blast fired."}
        </li>
      </ul>
    </div>
  ) : (
    <>
      <PackDetailsForm
        details={details}
        setDetails={setDetails}
        namePlaceholder="IPL 2026 — Full squads"
        shortDescriptionPlaceholder="10 IPL franchises with full 2026 squads"
        testMode={testMode}
        setTestMode={setTestMode}
      />

      <div className="pt-3 border-t border-border-default">
        <div className="text-xs uppercase tracking-widest text-text-secondary mb-2">
          Composition
        </div>
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-text-primary font-medium flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Teams
          </span>
          <span className="font-mono">{teamIds.length}</span>
        </div>
        <div className="flex items-center justify-between text-sm mb-3">
          <span className="text-text-primary font-medium">Players (total)</span>
          <span className="font-mono">{totalPlayers}</span>
        </div>
        {teamIds.length > 0 && (
          <ul className="space-y-1 text-xs text-text-secondary">
            {teamIds.map((tid) => {
              const t = teamById[tid];
              const n = playerIdsByTeam[tid]?.length || 0;
              return (
                <li key={tid} className="flex items-center justify-between gap-2">
                  <span className="truncate">{t?.short_name || t?.name || "Team"}</span>
                  <span className="font-mono">{n}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {savedAt && (
        <div className="pt-3 border-t border-border-default text-xs text-emerald-300 flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Draft saved · {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </>
  );

  const isSaved = Boolean(packId);
  return (
    <WizardShell
      eyebrow="Marketplace · SQUAD_PACK"
      title={result ? "Squad pack created" : "New squad bundle"}
      onClose={onClose}
      busy={busy}
      error={error}
      sidebar={sidebar}
      primaryLabel={result ? "Done" : (isSaved ? "Publish" : "Create & publish")}
      primaryIcon={result ? null : <Sparkles className="w-4 h-4" />}
      primaryDisabled={!result && (!detailsValid() || teamIds.length === 0)}
      primaryTone="amber"
      onPrimary={result ? onClose : handleCreate}
      secondaryLabel={result ? undefined : (isSaved ? "Update draft" : "Save draft")}
      secondaryIcon={result ? null : <Save className="w-4 h-4" />}
      secondaryDisabled={!detailsValid() || teamIds.length === 0}
      onSecondary={handleSaveDraft}
    >
      {!result ? (
        <>
          {/* 1) Team picker */}
          <section className="space-y-3">
            <div>
              <h3 className="text-base font-semibold">Add teams from the catalog</h3>
              <p className="text-sm text-text-secondary mt-0.5">
                Only SYSTEM-owned teams the platform has already ingested are
                shown. Each pick prefills its master squad into an editable
                roster card below.
              </p>
            </div>
            <CatalogPicker
              fetchFn={(params) => listCatalogTeams(params)}
              selectedIds={teamIds}
              onChange={setTeamIds}
              onToggle={handleTeamToggle}
              placeholder="Search teams by name or short name…"
              emptyHint="No teams match. Try a different search."
              renderRow={(t) => (
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <span className="font-medium">{t.name}</span>
                    {t.short_name && (
                      <span className="ml-2 text-xs text-text-secondary font-mono">
                        {t.short_name}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-text-secondary shrink-0 flex items-center gap-2">
                    {t.team_type && <span>{t.team_type}</span>}
                    {t.nationality_name && <span>· {t.nationality_name}</span>}
                    <span className="inline-flex items-center gap-0.5">
                      <Users className="w-3 h-3" /> {(t.master_squad_ids || []).length}
                    </span>
                  </div>
                </div>
              )}
              renderChip={(t) => <span>{t.short_name || t.name}</span>}
            />
          </section>

          {/* 2) Per-team roster cards */}
          {teamIds.length > 0 && (
            <section className="space-y-4">
              <div className="flex items-baseline justify-between">
                <h3 className="text-base font-semibold">
                  Team rosters
                  <span className="ml-2 text-sm font-normal text-text-secondary">
                    {totalPlayers} player{totalPlayers === 1 ? "" : "s"} across {teamIds.length} team{teamIds.length === 1 ? "" : "s"}
                  </span>
                </h3>
              </div>
              <div className="space-y-5">
                {teamIds.map((tid) => {
                  const t = teamById[tid];
                  const roster = playerIdsByTeam[tid] || [];
                  const isCollapsed = !!collapsed[tid];
                  return (
                    <div
                      key={tid}
                      className="rounded-lg border border-border-default bg-bg-secondary/40 overflow-hidden"
                    >
                      {/* Team card header */}
                      <div className="px-5 py-3 border-b border-border-default flex items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => toggleCollapse(tid)}
                          className="flex items-center gap-2 min-w-0 text-left hover:text-button-primary"
                        >
                          {isCollapsed
                            ? <ChevronRight className="w-4 h-4 shrink-0" />
                            : <ChevronDown className="w-4 h-4 shrink-0" />}
                          <span className="text-base font-semibold truncate">
                            {t?.name || "Team"}
                          </span>
                          {t?.short_name && (
                            <span className="text-xs text-text-secondary font-mono">
                              {t.short_name}
                            </span>
                          )}
                          {t?.nationality_name && (
                            <span className="text-xs text-text-secondary">
                              · {t.nationality_name}
                            </span>
                          )}
                        </button>
                        <div className="flex items-center gap-2 shrink-0 text-xs">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bg-primary border border-border-default">
                            <Users className="w-3 h-3" /> {roster.length} player{roster.length === 1 ? "" : "s"}
                          </span>
                          <button
                            type="button"
                            onClick={() => resetToMasterSquad(tid)}
                            title="Reset roster to the team's master squad"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border-default text-text-secondary hover:text-text-primary hover:bg-bg-secondary"
                          >
                            <RotateCcw className="w-3 h-3" /> Reset
                          </button>
                          <button
                            type="button"
                            onClick={() => removeTeam(tid)}
                            title="Remove this team from the pack"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                          >
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </div>

                      {/* Player picker inside the team card */}
                      {!isCollapsed && (
                        <div className="px-5 py-4">
                          <CatalogPicker
                            fetchFn={(params) => listCatalogPlayers(params)}
                            selectedIds={roster}
                            onChange={(ids) => setPlayersForTeam(tid, ids)}
                            placeholder={`Add / remove players in ${t?.short_name || "this team"}…`}
                            emptyHint="No players match. Try a different search."
                            renderRow={(p) => (
                              <div className="flex items-baseline justify-between gap-2">
                                <div className="min-w-0 flex items-center gap-2">
                                  <span className="font-medium">{playerDisplay(p)}</span>
                                  {p.role && (
                                    <span className={`text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${ROLE_TONE[p.role] || ""}`}>
                                      {roleBadge(p.role)}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-text-secondary shrink-0">
                                  {[p.gender, p.nationality_name].filter(Boolean).join(" · ")}
                                </div>
                              </div>
                            )}
                            renderChip={(p) => <span>{playerDisplay(p)}</span>}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-emerald-200 flex items-center gap-2 font-semibold">
            <CheckCircle2 className="w-5 h-5" /> Created “{result.pack_name}”
          </div>
          {result.teams.length > 0 && (
            <div className="rounded-md border border-border-default bg-bg-secondary/40">
              <div className="px-3 py-2 text-xs uppercase tracking-wider text-text-secondary border-b border-border-default">
                Teams in this pack
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {result.teams.map((t) => (
                    <tr key={t.team_id} className="border-t border-border-default first:border-t-0">
                      <td className="px-3 py-1.5">{t.name}</td>
                      <td className="px-3 py-1.5 font-mono text-text-secondary">
                        {t.player_count} player{t.player_count === 1 ? "" : "s"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.errors.length > 0 && (
            <details className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-200 text-sm">
              <summary className="cursor-pointer font-medium">
                {result.errors.length} row{result.errors.length === 1 ? "" : "s"} skipped
              </summary>
              <ul className="mt-2 ml-3 space-y-1 text-xs">
                {result.errors.slice(0, 20).map((e, i) => (
                  <li key={i}>
                    {e.team_id && `team ${e.team_id.slice(0, 8)}…: `}
                    {e.player_id && `player ${e.player_id.slice(0, 8)}…: `}
                    {e.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </WizardShell>
  );
}
